/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ComponentFactoryResolver, ComponentRef, Injectable, Renderer2 } from '@angular/core';

import { Events } from 'ag-grid-community';
// tslint:disable-next-line:no-submodule-imports
import { ProcessRowParams } from 'ag-grid-community/dist/lib/entities/gridOptions';

import { GridFeatureReadyParams } from '../feature.interfaces';
import { AbstractGridOptionBarFeature } from './abstract-grid-option-bar-feature';
import { GridRowInitParams, GridRowOptionsComponent } from './grid-row-options-component';

interface OptionBarConfig {
  componentRef: ComponentRef<GridRowOptionsComponent>;
  row: GridRowInitParams;
}

@Injectable()
export class GridOptionBarFeature extends AbstractGridOptionBarFeature {
  private optionBars: { [id: string]: OptionBarConfig } = {};

  private nextFrame: number = 0;

  constructor(protected resolver: ComponentFactoryResolver,
              protected renderer: Renderer2) {
    super();

    this.scheduleUpdatePosition = this.scheduleUpdatePosition.bind(this);
  }

  onGridReady(params: GridFeatureReadyParams): void {
    super.onGridReady(params);

    // TODO: think about debouncing of event handler
    this.api.addEventListener(Events.EVENT_COLUMN_RESIZED, this.scheduleUpdatePosition);
    this.api.addEventListener(Events.EVENT_MODEL_UPDATED, this.scheduleUpdatePosition);
    this.api.addEventListener(Events.EVENT_GRID_SIZE_CHANGED, this.scheduleUpdatePosition);
    this.getScrollableContainer().addEventListener('scroll', this.scheduleUpdatePosition);
  }

  onDestroy(): void {
    if (this.api) {
      this.getScrollableContainer().removeEventListener('scroll', this.scheduleUpdatePosition);

      cancelAnimationFrame(this.nextFrame);
    }
  }

  process<C extends GridRowOptionsComponent>(params: ProcessRowParams): void {
    if (!params.eRow) {
      return;
    }

    this.createAndAttachComponent(params);

    params.addRenderedRowListener(Events.EVENT_VIRTUAL_ROW_REMOVED, () => {
      this.detachAndDestroyComponent(params);
    });
  }

  private scheduleUpdatePosition(): void {
    if (this.nextFrame) {
      return;
    }

    this.nextFrame = requestAnimationFrame(() => {
      this.nextFrame = 0;
      this.updatePosition();
    });
  }

  private updatePosition(): void {
    Object.keys(this.optionBars).forEach((id) => {
      const optionBar = this.optionBars[id];

      this.renderer.setStyle(
        optionBar.componentRef.location.nativeElement,
        'right',
        this.getBestRightPosition(optionBar.row) + 'px',
      );
    });
  }

  private createAndAttachComponent<C extends GridRowOptionsComponent>(row: GridRowInitParams): void {
    const component = this.optionBars[row.node.id];
    if (component) {
      this.detachAndDestroyComponent(row);
    }

    const componentRef = this.createComponent();

    this.attachComponent(row, componentRef);

    this.optionBars[row.node.id] = { row, componentRef };
  }

  private detachAndDestroyComponent(row: GridRowInitParams): void {
    this.detachComponent(row);

    const optionBar = this.optionBars[row.node.id];

    if (optionBar) {
      optionBar.componentRef.destroy();
      delete this.optionBars[row.node.id];
    }
  }

}
